// Вокабуляр ЯЗЫКА каталога (`pothos_assets.locale`).
//
// ЛОКАЛЬ = ЯЗЫК + СТРАНА (правило Глеба, 2026-08-24): es_US, es_ES и es_MX —
// разные локали, и мерчант, закрытый в одной, в остальных открыт. Страна живёт
// в отдельной колонке и канонизируется через ./country, здесь — только язык.
//
// Это ВОКАБУЛЯР ХРАНЕНИЯ: он обязан принимать всё, что реально лежит в колонке,
// включая исторический разнобой. Замер по карточным категориям на 2026-08-24:
//   'Bahasa Indonesia' 380 против 'Indonesian' 1110
//   'Portuguese_Brazil' 237 против 'Portuguese' 1691
//   'Malay' 20 против 'Malaysian' 1195
//   ISO-коды вместо имён: 'es' 44, 'da' 33, 'FR' 19, 'sv' 1, 'fi' 1
//   'Chinese' 3, и один ассет с 'en' (английский — не язык сбора)
// Без сведения к канону 'Bahasa Indonesia' и 'Indonesian' дают мерчанту ДВЕ
// ячейки капа, то есть 40 слотов вместо 20 — и это не падает, а тихо
// пропускает лишнее.

import { PLAN_LOCALES } from './plan';

/** ISO-2 языка → имя языка в вокабуляре каталога. */
export const CATALOG_LOCALE_NAME: Record<string, string> = {
  ar: 'Arabic', da: 'Danish', de: 'German', es: 'Spanish', fi: 'Finnish',
  fr: 'French', he: 'Hebrew', hi: 'Hindi', id: 'Indonesian', it: 'Italian',
  ja: 'Japanese', ko: 'Korean', ms: 'Malaysian', nl: 'Dutch', no: 'Norwegian',
  pl: 'Polish', pt: 'Portuguese', sv: 'Swedish', th: 'Thai', tr: 'Turkish',
  vi: 'Vietnamese', en: 'English',
};

// Синонимы и «страновые» написания, означающие в каталоге тот же язык. Суффикс
// страны здесь избыточен: пара (Portuguese, Brazil) уже И ЕСТЬ pt_BR.
const LOCALE_ALIASES: Record<string, string> = {
  bahasaindonesia: 'Indonesian', bahasa: 'Indonesian', indonesian: 'Indonesian',
  malay: 'Malaysian', malaysian: 'Malaysian',
  portuguesebrazil: 'Portuguese', portuguesebr: 'Portuguese',
  brazilianportuguese: 'Portuguese',
  chinese: 'Simplified_Chinese', mandarin: 'Simplified_Chinese',
  simplifiedchinese: 'Simplified_Chinese',
  traditionalchinese: 'Traditional_Chinese',
  norwegian: 'Norwegian', bokmal: 'Norwegian',
};

/**
 * Любое написание языка → канон каталога.
 * Принимает код локали ('es_ES', 'zh_TW', 'ZH-HANS'), имя ('Spanish',
 * 'Bahasa Indonesia') и голый ISO-2 ('es'). Неизвестное → '' (не выдумываем).
 */
export function canonLocaleName(raw: string): string {
  const s = String(raw || '').trim();
  if (!s) return '';
  const low = s.toLowerCase();

  // Коды письменности несут не страну, а систему письма.
  if (low.includes('hans')) return 'Simplified_Chinese';
  if (low.includes('hant')) return 'Traditional_Chinese';

  // Код локали 'es_ES' → языковая часть; у китайского страна выбирает письмо.
  if (/^[a-z]{2}[_-][a-z]{2}$/i.test(s)) {
    const [lang, ctry] = s.split(/[_-]/);
    const l = lang.toLowerCase();
    if (l === 'zh') {
      const c = ctry.toUpperCase();
      return c === 'SG' || c === 'CN' ? 'Simplified_Chinese' : 'Traditional_Chinese';
    }
    return CATALOG_LOCALE_NAME[l] || '';
  }

  // Голый ISO-2.
  if (/^[a-z]{2}$/i.test(s)) {
    return low === 'zh' ? 'Simplified_Chinese' : (CATALOG_LOCALE_NAME[low] || '');
  }

  // Имя языка: алиасы, иначе нормализация регистра и разделителей.
  const flat = low.replace(/[^a-z]/g, '');
  if (LOCALE_ALIASES[flat]) return LOCALE_ALIASES[flat];
  return s.replace(/\s+/g, '_').split('_')
    .map(p => (p ? p[0].toUpperCase() + p.slice(1).toLowerCase() : p)).join('_');
}

// ─── Языки сбора страны ───────────────────────────────────────────────────────
// Камерные задачи локаль не выбирают, поэтому язык предполагается по стране
// съёмки («язык задаёт страна сбора»). Это ТОЛЬКО умолчание для показа «что уже
// закрыто»: фактическую ячейку кадра решает язык, который eval прочитал НА
// САМОЙ КАРТЕ. Швейцария — та же страна, но итальянская карта идёт в it_CH,
// а не в de_CH, даже если de_CH закрыт.
//
// Карта ВЫВОДИТСЯ из реестра плановых локалей (./plan) — до аудита 2026-08-25
// здесь жил собственный список, и он разошёлся с планом: держал Норвегию
// (страны сбора среди 35 нет) и не знал норвежского в Британии, Дании и
// Швеции (no_GB / no_DK / no_SE).
const COLLECTION_LANGS: Record<string, string[]> = {};
for (const l of PLAN_LOCALES) {
  const langs = (COLLECTION_LANGS[l.country] ||= []);
  if (!langs.includes(l.language)) langs.push(l.language);
}

/**
 * Языки сбора страны в каноне каталога. Принимает имя страны каталога
 * ('United_States'). Пусто = страна не собирается — в том числе Norway.
 */
export function collectionLangsForCountry(countryName: string): string[] {
  return COLLECTION_LANGS[String(countryName || '').trim()] || [];
}

/**
 * Карточные категории каталога, между которыми кап мерчанта ОБЩИЙ.
 * 20 мембершипов McDonald's в sv_SE закрывают McDonald's и для гифт-карт той же
 * локали (Глеб, 2026-08-24): «наша цель — читать между категорий, а не внутри
 * одной». Билеты (event_ticket) в кап мерчанта не входят вообще.
 */
export const MERCHANT_CAP_CATEGORIES = ['gift_card', 'coupon', 'membership'] as const;
